<script setup>
import Button from 'next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Switch from 'next/switch/Switch.vue';
import { computed, ref, watch } from 'vue';

const props = defineProps({
  tool: {
    type: Object,
    required: true,
  },
  builtIn: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['clone', 'edit', 'delete', 'toggleActive']);

const toolActive = ref(props.tool.active);

const isAutomationType = computed(() => {
  return props.tool.type === 'function_automation';
});

const isClickable = computed(() => {
  return !props.builtIn && isAutomationType.value;
});

const onToolEnabledChange = value => {
  emit('toggleActive', value);
};

watch(
  () => props.tool,
  ({ active }) => {
    toolActive.value = active;
  }
);
</script>

<template>
  <div
    class="flex items-center gap-4 px-6 py-4 transition-colors"
    :class="[
      isClickable ? 'cursor-pointer hover:bg-n-alpha-1' : '',
      !builtIn && !isAutomationType ? 'opacity-50 pointer-events-none' : '',
    ]"
    @click="isClickable && emit('edit')"
  >
    <div
      class="size-9 flex-shrink-0 rounded-lg bg-n-alpha-3 flex items-center justify-center"
    >
      <Icon
        :icon="tool.icon || 'i-lucide-wrench'"
        class="size-4 text-n-slate-11"
      />
    </div>

    <div class="flex flex-col gap-0.5 flex-1 min-w-0">
      <span class="text-heading-3 text-n-slate-12 truncate">
        {{ tool.name }}
      </span>
      <span class="text-body-main text-n-slate-11 truncate">
        {{ tool.description }}
      </span>
    </div>

    <div v-if="builtIn" class="flex items-center flex-shrink-0" @click.stop>
      <Switch v-model="toolActive" @change="onToolEnabledChange" />
    </div>

    <div
      v-else-if="isAutomationType"
      class="flex items-center gap-2 flex-shrink-0"
      @click.stop
    >
      <Switch v-model="toolActive" @change="onToolEnabledChange" />

      <div class="mx-1 w-px h-4 bg-n-weak" />

      <Button
        v-tooltip.top="'Edit'"
        icon="i-woot-edit-pen"
        slate
        sm
        @click="emit('edit')"
      />
      <Button
        v-tooltip.top="'Clone'"
        icon="i-woot-clone"
        slate
        sm
        @click="emit('clone')"
      />
      <Button
        v-tooltip.top="'Delete'"
        icon="i-woot-bin"
        slate
        sm
        class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
        @click="emit('delete')"
      />
    </div>
  </div>
</template>
